import React, { useEffect, useState } from "react";
import { useLanguage } from "../../../../Authentication/LanguageContext";
import ServerUrl from "../../../../../BACKEND";
import "./Vegetables.css";

interface VegetableItem {
  id: number;
  german: string;
  sentence: string;
  audio: string | null;
  image: string | null;
  tamil: string;
  english: string;
  sinhala: string;
  hindi: string;
  arabic: string;
  turkish: string;
  russian: string;
  chinese: string;
  french: string;
  spanish: string;
  [key: string]: string | number | null;
}

const capitalize = (text: string) =>
  text ? text.charAt(0).toUpperCase() + text.slice(1) : "";

const Vegetables: React.FC = () => {
  const [data, setData] = useState<VegetableItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentlyPlaying, setCurrentlyPlaying] = useState<string | null>(null);
  const { language } = useLanguage();

  useEffect(() => {
    fetch(`${ServerUrl}/api/vegetables`)
      .then((res) => res.json())
      .then((json) => setData(json))
      .catch((err) => console.error("Error fetching vegetables:", err))
      .finally(() => setLoading(false));
  }, [language]);

  const getTranslation = (item: VegetableItem) => {
    const map: Record<string, string> = {
      en: "english",
      de: "german",
      ta: "tamil",
      si: "sinhala",
      hi: "hindi",
      ar: "arabic",
      tr: "turkish",
      ru: "russian",
      zh: "chinese",
      fr: "french",
      es: "spanish",
    };
    return capitalize(String(item[map[language] || "english"] || ""));
  };

  const playAudioOrTTS = (item: VegetableItem) => {
    if (currentlyPlaying === item.german) {
      setCurrentlyPlaying(null);
      return;
    }

    setCurrentlyPlaying(item.german);

    if (item.audio) {
      const audio = new Audio(`${ServerUrl}/storage/${item.audio}`);
      audio.play();
      audio.onended = () => setCurrentlyPlaying(null);
      audio.onerror = () => {
        speak(item.german);
      };
    } else {
      speak(item.german);
    }
  };

  const speak = (text: string) => {
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = "de-DE";
    utterance.onend = () => setCurrentlyPlaying(null);
    speechSynthesis.speak(utterance);
  };

  const getImageUrl = (image: string | null) => {
    if (!image) return `${ServerUrl}/storage/A1Image/placeholder.png`;
    if (image.startsWith("http")) return image;
    return `${ServerUrl}/storage/${image}`;
  };

  const titleMap = {
    en: "Vegetables",
    de: "Gemüse",
    ta: "காய்கறிகள்",
    si: "එළවළු",
    hi: "सब्जियां",
    ar: "خضروات",
    tr: "Sebzeler",
    ru: "Овощи",
    zh: "蔬菜",
    fr: "Légumes",
    es: "Verduras",
  };

  const subtitleMap = {
    en: "Vegetables in German",
    de: "Gemüse auf Deutsch",
    ta: "ஜெர்மன் மொழியில் காய்கறிகள்",
    si: "ජර්මන් භාෂාවෙන් එළවළු",
    hi: "जर्मन में सब्जियां",
    ar: "الخضروات بالألمانية",
    tr: "Almanca'da Sebzeler",
    ru: "Овощи на немецком",
    zh: "德语蔬菜",
    fr: "Légumes en allemand",
    es: "Verduras en alemán",
  };

  return (
    <div className="vegetables-page-container">
      <div className="vegetables-page-header">
        <div>
          <h1 className="vegetables-page-title">{titleMap["de"]}</h1>
          <p className="vegetables-page-subtitle">{subtitleMap[language]}</p>
        </div>
      </div>

      {loading ? (
        <div className="vegetables-page-loading">Loading...</div>
      ) : (
        <div className="vegetables-page-grid">
          {data.map((item) => {
            const isPlaying = currentlyPlaying === item.german;
            return (
              <div
                key={item.id}
                className={`vegetables-page-card ${
                  isPlaying ? "vegetables-page-playing" : ""
                }`}
                onClick={() => playAudioOrTTS(item)}
              >
                <div className="vegetables-page-image-wrapper">
                  <img
                    src={getImageUrl(item.image)}
                    alt={item.german}
                    className="vegetables-page-image"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = `${ServerUrl}/storage/A1Image/placeholder.png`;
                    }}
                  />
                </div>
                <div className="vegetables-page-german-text">
                  {capitalize(item.german)}
                </div>
                <div className="vegetables-page-translation-text">
                  {getTranslation(item)}
                </div>
                <div className="vegetables-page-sentence-text">{item.sentence}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Vegetables;
